/**
 * Utility functions for URL detection and conversion
 * Handles automatic link detection in text messages
 */

import React from 'react';

/**
 * Detects URLs and domains in text and converts them to clickable links
 * @param text - The text content to process
 * @returns JSX element with clickable links
 */
export const convertUrlsToLinks = (text: string): JSX.Element => {
    // Enhanced regex to match various URL patterns and domains
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?)/gi;

    const parts = text.split(urlRegex);

    return (
        <>
            {parts.map((part, index) => {
                if (urlRegex.test(part)) {
                    // Normalize the URL for the href
                    let href = part;
                    if (!part.startsWith('http://') && !part.startsWith('https://')) {
                        href = part.startsWith('www.') ? `https://${part}` : `https://${part}`;
                    }

                    return (
                        <a
                            key={index}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="barca-chat-link text-blue-300 hover:text-blue-200 underline underline-offset-2 transition-colors duration-200"
                            onClick={(e) => {
                                e.stopPropagation(); // Prevent any parent click handlers
                            }}
                        >
                            {part}
                        </a>
                    );
                }
                return <span key={index}>{part}</span>;
            })}
        </>
    );
};

/**
 * Check if a string contains any URLs or domains
 * @param text - Text to check for URLs
 * @returns boolean indicating if URLs were found
 */
export const containsUrls = (text: string): boolean => {
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?)/gi;
    return urlRegex.test(text);
};
